// WebAudio graph that turns the caller-owned microphone and remote tracks into one recordable stream.
//
// MediaRecorder records a fixed stream and stops or errors when that stream's tracks change, while
// the call engine replaces tracks on device switches and renegotiation. The graph's output track
// stays the same for the whole capture; inputs are swapped behind it. Layout is stereo:
// left = this computer's microphone, right = the call audio as heard. A missing input is silence.
//
// The graph never stops, mutes or disables a caller-owned track. It only wraps each one in its own
// MediaStream for a source node and disconnects that node on swap and close. The context is never
// connected to `destination`, so nothing is played.

export interface CaptureGraph {
  /** The stream to record. Its single audio track is owned by the graph. */
  readonly stream: MediaStream;
  setLocal(track: MediaStreamTrack | null): void;
  setRemote(track: MediaStreamTrack | null): void;
  close(): Promise<void>;
}

export type CaptureGraphFactory = () => CaptureGraph;

export type AudioContextCtor = new (options?: AudioContextOptions) => AudioContext;

export function createWebAudioGraph(Ctor: AudioContextCtor = AudioContext): CaptureGraph {
  const ctx = new Ctor({ latencyHint: 'playback', sampleRate: 48_000 });
  const out = ctx.createMediaStreamDestination();
  out.channelCount = 2;
  out.channelCountMode = 'explicit';
  out.channelInterpretation = 'discrete';
  const merger = ctx.createChannelMerger(2);
  merger.connect(out);

  // One mono down-mix per side, so a stereo remote stream still lands on a single channel.
  const sides = [0, 1].map(channel => {
    const mono = ctx.createGain();
    mono.channelCount = 1;
    mono.channelCountMode = 'explicit';
    mono.channelInterpretation = 'speakers';
    mono.connect(merger, 0, channel);
    return { mono, source: null as MediaStreamAudioSourceNode | null, track: null as MediaStreamTrack | null };
  });

  let closed = false;
  const attach = (side: (typeof sides)[number], track: MediaStreamTrack | null) => {
    if (closed || side.track === track) return;
    side.source?.disconnect();
    side.source = null;
    side.track = track;
    if (track && track.kind === 'audio' && track.readyState === 'live') {
      side.source = ctx.createMediaStreamSource(new MediaStream([track]));
      side.source.connect(side.mono);
    }
  };

  // A context created without a user gesture may start suspended; the recorder would then see silence.
  if (ctx.state === 'suspended') void ctx.resume().catch(() => {});

  return {
    stream: out.stream,
    setLocal: track => attach(sides[0]!, track),
    setRemote: track => attach(sides[1]!, track),
    async close() {
      if (closed) return;
      closed = true;
      for (const side of sides) {
        side.source?.disconnect();
        side.source = null;
        side.track = null;
        side.mono.disconnect();
      }
      merger.disconnect();
      // Only the graph's own output track is stopped; caller-owned input tracks are left alone.
      for (const t of out.stream.getTracks()) t.stop();
      await ctx.close().catch(() => {});
    },
  };
}
